import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { useForm, FormProvider } from 'react-hook-form';
import { BaseBreadcrumbs } from '@mgm/common/components/base';
import { useQueryClient } from '@tanstack/react-query';
import { PAGES_ANALYTICS_TITLES, QUERY_KEYS } from '../../utils/enums';
import { PageHelmet } from '../../components';
import { ROUTES } from '../../data';
import { CustomRangePicker } from '../../components/pickers';
import { useAppSelector } from '../../hooks/useAppStore';
import { getOrdersChosenDates } from '../../store/orders/selectors';
import { useOrders } from '../../features/orders/Shared/hooks';
import { useOrdersByDates } from '../../features/orders/Shared/hooks/useOrdersByDates';
import DailyStats from '../../features/orders/DailyStats';
import { useCommonOrders } from '../../hooks/useCommonOrders';
import { OrdersCountLoader } from '../../features/orders/Shared/components';
import { getTokenDetails } from '../../store/authenticate/selectors';

const CommissionAnalyticsPage = () => {
  const { t } = useTranslation('nav');
  const { settings } = useAppSelector(getTokenDetails);

  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const {
    ordersSetDates,
    isBtnDisabled,
    setIsBtnDisabled,
  } = useOrders();

  const queryClient = useQueryClient();

  const isOrdersInCache = !!queryClient.getQueryData([QUERY_KEYS.ORDERS.GET_ORDERS]);

  const { setIsFetchByDate } = useCommonOrders({
    prevTab: isOrdersInCache ? '1' : '',
  });

  const { getOrdersByDates } = useOrdersByDates(setIsBtnDisabled, setIsFetchByDate);

  const methods = useForm({
    mode: 'all',
    values: {
      dateRange: ordersChosenDates,
    },
  });

  return (
    <PageHelmet title={PAGES_ANALYTICS_TITLES.ANALYTICS.COMMISSION}>
      <Container
        sx={{
          px: {
            xs: 0, lg: 2, md: 2, sm: 2,
          },
        }}
        maxWidth={false}
      >
        <BaseBreadcrumbs
          heading={t('individualCommissionsAnalytics')}
          links={[
            { name: t('analytics'), href: ROUTES.ANALYTICS.COMMISSION_INDIVIDUAL },
            { name: t('individualCommissionsAnalytics'), href: ROUTES.ANALYTICS.COMMISSION_INDIVIDUAL },
          ]}
          action={(
            <Stack>
              <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
                <FormProvider {...methods}>
                  <CustomRangePicker
                    format={settings.ui?.dateFormat}
                    defaultValue={ordersChosenDates}
                    setDates={ordersSetDates}
                  />
                  <LoadingButton
                    disabled={isBtnDisabled}
                    onClick={getOrdersByDates}
                    size="small"
                    variant="contained"
                  >
                    {t('load', { ns: 'common' })}
                  </LoadingButton>
                </FormProvider>
              </Stack>
              <OrdersCountLoader isDisplayDivider={false} />
            </Stack>
        )}
        />
        <DailyStats />
      </Container>
    </PageHelmet>
  );
};

export default CommissionAnalyticsPage;
